import type React from 'react';
import { useEffect, useRef, useState } from 'react';

interface WaitingForHostPageProps {
  meetingId: string;
  errorDetail?: string;
  onRetry: () => void;
  onBackToHome: () => void;
  autoRetryIntervalMs?: number;
}

const AUTO_RETRY_DEFAULT_MS = 6000;

export const WaitingForHostPage: React.FC<WaitingForHostPageProps> = ({
  meetingId,
  errorDetail,
  onRetry,
  onBackToHome,
  autoRetryIntervalMs = AUTO_RETRY_DEFAULT_MS,
}) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Use a ref to always hold the latest onRetry without re-creating the interval
  const onRetryRef = useRef(onRetry);
  useEffect(() => {
    onRetryRef.current = onRetry;
  }, [onRetry]);

  useEffect(() => {
    const interval = setInterval(() => {
      onRetryRef.current();
    }, autoRetryIntervalMs);

    return () => clearInterval(interval);
  }, [autoRetryIntervalMs]); // interval only re-creates if the duration changes

  return (
    <main className="waiting-for-host" aria-labelledby="waiting-for-host-title">
      <div className="waiting-for-host__card">
        <div className="waiting-for-host__icon-wrapper">
          <div className="waiting-for-host__spinner" aria-hidden="true" />
        </div>

        <p className="waiting-for-host__label">Joining meeting</p>
        <p className="waiting-for-host__meeting-id">{meetingId}</p>

        <h1 id="waiting-for-host-title" className="waiting-for-host__title">
          This meeting has not yet started
          {errorDetail && (
            <button
              type="button"
              className="waiting-for-host__info-icon"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
              onFocus={() => setShowTooltip(true)}
              onBlur={() => setShowTooltip(false)}
              aria-label="Show error details"
            >
              &#9432;
              {showTooltip && (
                <span className="waiting-for-host__tooltip" role="tooltip">
                  {errorDetail}
                </span>
              )}
            </button>
          )}
        </h1>

        <p className="waiting-for-host__description">
          You will be automatically joined once the meeting starts.
        </p>

        <div className="waiting-for-host__actions">
          <button
            type="button"
            onClick={onRetry}
            className="waiting-for-host__button waiting-for-host__button--primary"
          >
            Retry
          </button>
          <button
            type="button"
            onClick={onBackToHome}
            className="waiting-for-host__button waiting-for-host__button--secondary"
          >
            Back to Home
          </button>
        </div>
      </div>
    </main>
  );
};
